import { useEffect, useState } from "react";
import api, { BACKEND_URL, formatApiError } from "../../lib/api";
import { toast } from "sonner";
import { Button } from "../ui/button";
import MediaEditor from "./MediaEditor";
import {
  FolderOpen, Film, Image as ImageIcon, RefreshCcw, Trash2, Pencil, Loader2, Clock, Sparkles,
} from "lucide-react";

function toAbsolute(url) {
  if (!url) return url;
  if (url.startsWith("http") || url.startsWith("blob:") || url.startsWith("data:")) return url;
  return `${BACKEND_URL}${url}`;
}

function fmtRel(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const now = Date.now();
  const s = Math.round((now - then) / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60); if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60); if (h < 24) return `${h}h ago`;
  const d = Math.round(h / 24); if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

export default function DraftsPanel({ onExportUpload }) {
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [resuming, setResuming] = useState(null);
  const [editing, setEditing] = useState(null); // { file, draftId }

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/studio/drafts");
      setDrafts(r.data?.drafts || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Couldn't load drafts");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const resume = async (d) => {
    if (!d.source_url || d.source_url.startsWith("local://")) {
      toast.error("This draft was never saved with the original file. Re-upload to continue.");
      return;
    }
    setResuming(d.id);
    try {
      const abs = toAbsolute(d.source_url);
      const resp = await fetch(abs);
      if (!resp.ok) throw new Error(`Fetch failed ${resp.status}`);
      const blob = await resp.blob();
      const guessedType = blob.type || (d.kind === "video" ? "video/mp4" : "image/jpeg");
      const guessedExt = guessedType.includes("mp4") ? "mp4"
                       : guessedType.includes("webm") ? "webm"
                       : guessedType.includes("quicktime") ? "mov"
                       : guessedType.includes("png") ? "png"
                       : guessedType.includes("webp") ? "webp"
                       : d.kind === "video" ? "mp4" : "jpg";
      const name = d.title || `draft.${guessedExt}`;
      const file = new File([blob], name, { type: guessedType });
      setEditing({ file, draftId: d.id });
    } catch (e) {
      toast.error("Couldn't load the source file. It may have been deleted.");
    } finally { setResuming(null); }
  };

  const del = async (d) => {
    if (!window.confirm("Delete this draft? This can't be undone.")) return;
    try {
      await api.delete(`/studio/drafts/${d.id}`);
      setDrafts((ds) => ds.filter((x) => x.id !== d.id));
      toast.success("Draft removed");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    }
  };

  const onEditorExport = (exportedFile, meta) => {
    setEditing(null);
    if (onExportUpload) onExportUpload(exportedFile, meta);
    // Optional: refresh drafts (edit state may have changed if user saved before export)
    load();
  };

  const onEditorCancel = () => {
    setEditing(null);
    load();
  };

  return (
    <div className="space-y-4" data-testid="drafts-panel">
      {editing && (
        <MediaEditor
          file={editing.file}
          draftId={editing.draftId}
          onExport={onEditorExport}
          onCancel={onEditorCancel}
          onDraftSaved={() => load()}
        />
      )}

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <FolderOpen className="h-4 w-4 text-nexus-pink" />
          <span className="font-display font-bold tracking-tight">Your drafts</span>
          <span className="text-[10px] uppercase tracking-widest text-zinc-500">{drafts.length} saved</span>
        </div>
        <Button
          onClick={load}
          variant="outline"
          className="rounded-full border-white/10 text-white hover:bg-white/5 h-8"
          data-testid="drafts-refresh-btn"
        >
          <RefreshCcw className="h-3.5 w-3.5 mr-1.5" /> Refresh
        </Button>
      </div>

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="aspect-square rounded-xl bg-white/[0.03] animate-pulse" />
          ))}
        </div>
      ) : drafts.length === 0 ? (
        <div
          className="rounded-2xl border border-dashed border-white/10 bg-white/[0.02] p-8 text-center"
          data-testid="drafts-empty"
        >
          <Sparkles className="h-5 w-5 mx-auto mb-2 text-nexus-pink" />
          <div className="font-semibold">No drafts yet</div>
          <div className="text-xs text-zinc-500 mt-1">Save a draft mid-edit and it&apos;ll show up here — ready to resume from any device.</div>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="drafts-grid">
          {drafts.map((d) => {
            const abs = toAbsolute(d.thumb_url || d.source_url);
            const video = d.kind === "video";
            const isLocal = (d.source_url || "").startsWith("local://");
            return (
              <div
                key={d.id}
                className="relative aspect-square rounded-xl overflow-hidden hairline group bg-black"
                data-testid={`draft-item-${d.id}`}
              >
                {isLocal ? (
                  <div className="w-full h-full flex flex-col items-center justify-center text-zinc-500 gap-2 bg-white/[0.02]">
                    <ImageIcon className="h-6 w-6" />
                    <span className="text-[10px] uppercase tracking-widest">Source not persisted</span>
                  </div>
                ) : video ? (
                  <video src={abs} className="w-full h-full object-cover" muted playsInline preload="metadata" />
                ) : (
                  <img src={abs} alt="" className="w-full h-full object-cover" />
                )}

                {/* Meta strip */}
                <div className="absolute top-2 left-2 flex gap-1.5">
                  <span className="glass px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest flex items-center gap-1">
                    {video ? <Film className="h-3 w-3" /> : <ImageIcon className="h-3 w-3" />}
                    {video ? "Video" : "Image"}
                  </span>
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-2 bg-gradient-to-t from-black/85 via-black/60 to-transparent">
                  <div className="text-[11px] font-semibold truncate text-white">{d.title || "Untitled draft"}</div>
                  <div className="text-[10px] text-zinc-400 flex items-center gap-1"><Clock className="h-2.5 w-2.5" />{fmtRel(d.updated_at)}</div>
                </div>

                {/* Hover actions */}
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-2">
                  <Button
                    onClick={() => resume(d)}
                    disabled={resuming === d.id || isLocal}
                    className="nexus-gradient text-white border-0 rounded-full h-9 px-4 font-semibold disabled:opacity-50"
                    data-testid={`draft-resume-${d.id}`}
                  >
                    {resuming === d.id
                      ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Loading…</>
                      : <><Pencil className="h-3.5 w-3.5 mr-1.5" /> Resume</>}
                  </Button>
                  <button
                    onClick={() => del(d)}
                    className="text-[11px] uppercase tracking-widest text-red-300 hover:text-red-100 flex items-center gap-1"
                    data-testid={`draft-delete-${d.id}`}
                  >
                    <Trash2 className="h-3 w-3" /> Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
